import { useCallback, useMemo, useState } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import i18n from 'Pods/Shared/I18n'

import { CODE_LENGTH, MODE, OAUTH_PATHS, STEP } from '../constants'

const EMPTY_CODE = Array.from({ length: CODE_LENGTH }, () => '')
const REQUEST_PATH = '/guests/email_codes'
const VERIFY_PATH = '/guests/email_codes/verify'
const FALLBACK_REDIRECT = '/'
const SUCCESS_REDIRECT_DELAY_MS = 1500

function readConferenceEvent() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('conference') || null
}

// Owns the multi-step sign-in flow (auth -> code -> success): email/code state,
// the mocked-friendly /guests/email_codes request + verify calls, social OAuth
// redirects and the post-verify redirect. The component is left as pure view.
function useSignIn({
  initialMode = MODE.SIGNUP,
  initialStep = STEP.AUTH,
  locale = 'en' } = {},
) {
  const [mode, setMode] = useState(initialMode)
  const [step, setStep] = useState(initialStep)
  const [email, setEmail] = useState('')
  const [optIn, setOptIn] = useState(true)
  const [digits, setDigits] = useState(EMPTY_CODE)
  const [hasCodeError, setHasCodeError] = useState(false)

  const { postData } = useApiPost()
  const conferenceEvent = useMemo(readConferenceEvent, [])

  const handleEmailChange = useCallback((event) => {
    setEmail(event.target.value)
  }, [])

  const requestCode = useCallback(() => {
    postData({
      path: REQUEST_PATH,
      data: { email: email.trim(), conference_event: conferenceEvent },
      onSuccess: () => {
        setDigits(EMPTY_CODE)
        setHasCodeError(false)
        setStep(STEP.CODE)
      },
    })
  }, [postData, email, conferenceEvent])

  const handleSwitchMode = useCallback(() => {
    setMode(current => (current === MODE.SIGNUP ? MODE.LOGIN : MODE.SIGNUP))
    setStep(STEP.AUTH)
  }, [])

  const handleDigitsChange = useCallback((next) => {
    setHasCodeError(false)
    setDigits(next)

    if (!next.every(Boolean)) {
      return
    }

    postData({
      path: VERIFY_PATH,
      data: {
        email: email.trim(),
        code: next.join(''),
        conference_event: conferenceEvent,
        opt_in: optIn,
      },
      onSuccess: (response) => {
        const redirect = response?.data?.redirect || FALLBACK_REDIRECT
        setStep(STEP.SUCCESS)
        setTimeout(() => { window.location.assign(redirect) }, SUCCESS_REDIRECT_DELAY_MS)
      },
      onError: () => { setHasCodeError(true) },
    })
  }, [postData, email, conferenceEvent, optIn])

  const handleResend = useCallback(() => {
    setDigits(EMPTY_CODE)
    setHasCodeError(false)
    postData({
      path: REQUEST_PATH,
      data: { email: email.trim(), conference_event: conferenceEvent },
    })
  }, [postData, email, conferenceEvent])

  const handleChangeEmail = useCallback(() => {
    setDigits(EMPTY_CODE)
    setHasCodeError(false)
    setStep(STEP.AUTH)
  }, [])

  const handleSocial = useCallback((provider) => {
    const oauthPath = OAUTH_PATHS[provider]
    if (!oauthPath) return

    // OmniAuth 2.x only accepts the request phase as a POST with a CSRF token;
    // a GET navigation is rejected with "Not found. Authentication passthru.".
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const form = document.createElement('form')
    form.method = 'post'
    form.action = oauthPath

    if (token) {
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = token
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  }, [])

  const t = key => i18n.t(`auth_modal.${key}`, { ns: 'rails', lng: locale })

  let ariaLabel = mode === MODE.LOGIN ? t('aria_label.login') : t('aria_label.signup')
  if (step === STEP.SUCCESS) {
    ariaLabel = mode === MODE.LOGIN ? t('success.login_title') : t('success.signup_title')
  }

  return {
    mode,
    step,
    email,
    optIn,
    digits,
    hasCodeError,
    ariaLabel,
    setOptIn,
    handleEmailChange,
    requestCode,
    handleSwitchMode,
    handleDigitsChange,
    handleResend,
    handleChangeEmail,
    handleSocial,
  }
}

export default useSignIn
